'use client';

import { useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, Divider, IconButton, MenuList, Paper, Portal } from '@mui/material';

// Deep import, not the '@/components' barrel: this module is in the always-rendered
// graph, where a barrel import pulls the whole component tree (charts included).
import { DropdownItem } from '@/components/common/DropdownItem';
import { ThemeToggleItem } from '@/components/common/ThemeToggleItem';
import { useTheme as useAppTheme } from '@/contexts';
import { useAuth, useClickOutside, useMenuPosition, useToggle } from '@/hooks';
import { useAppDispatch } from '@/store';
import { openModal } from '@/store/slices/ui.slice';
import type { BaseMenuItemConfig } from '@/types';

import {
  LIGHT_THEME_ENABLED,
  PALETTE_KEYS,
  PRIMARY_MENU_ITEMS,
  ROUTES,
  SECONDARY_MENU_ITEMS,
} from '@/constants';

export const HamburgerMenu = () => {
  const router = useRouter();
  const { mode, toggleTheme } = useAppTheme();
  const isDarkMode = mode === 'dark';
  const { user } = useAuth();
  const dispatch = useAppDispatch();

  const [isOpen, toggle, setIsOpen] = useToggle(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen]);
  const menuRef = useClickOutside<HTMLDivElement>(handleClose, isOpen);

  const menuPosition = useMenuPosition(anchorRef, isOpen);

  const handleItemClick = useCallback(
    (item: BaseMenuItemConfig) => {
      if (item.path === ROUTES.REFERRALS && !user?.isReferredUser) {
        dispatch(openModal('referral'));
      } else if (item.action) {
        item.action();
      } else if (item.path) {
        router.push(item.path);
      }
      handleClose();
    },
    [router, handleClose, user?.isReferredUser, dispatch],
  );

  const handleStopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <>
      <Box ref={anchorRef} sx={{ position: 'relative' }}>
        <IconButton
          onClick={toggle}
          aria-label="Menu"
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            border: '1px solid',
            borderColor: PALETTE_KEYS.DIVIDER,
            bgcolor: PALETTE_KEYS.BACKGROUND_PAPER,
            color: PALETTE_KEYS.TEXT_SECONDARY,
            '&:hover': { bgcolor: PALETTE_KEYS.BACKGROUND_HOVER },
          }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>
      </Box>

      {isOpen && (
        <Portal>
          <Box
            onClick={handleClose}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 11999,
              bgcolor: 'transparent',
            }}
          />
          <Paper
            ref={menuRef}
            elevation={8}
            onClick={handleStopPropagation}
            sx={{
              position: 'fixed',
              top: menuPosition ? `${menuPosition.top}px` : 'auto',
              right: menuPosition ? `${menuPosition.right}px` : 16,
              minWidth: 220,
              zIndex: 12000,
              borderRadius: '12px',
              bgcolor: PALETTE_KEYS.BACKGROUND_PAPER,
              border: '1px solid',
              borderColor: PALETTE_KEYS.DIVIDER,
              boxShadow: (theme) =>
                theme.palette.mode === 'dark'
                  ? '0 10px 40px rgba(0, 0, 0, 0.6)'
                  : '0 10px 40px rgba(0, 0, 0, 0.15)',
              animation: 'fadeIn 0.2s ease-out',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(-8px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <MenuList sx={{ py: 1 }}>
              {PRIMARY_MENU_ITEMS.map((item) => (
                <DropdownItem key={item.label} item={item} onClick={handleItemClick} />
              ))}

              {LIGHT_THEME_ENABLED && (
                <ThemeToggleItem isDarkMode={isDarkMode} onToggle={toggleTheme} />
              )}

              <Divider sx={{ my: 1 }} />

              {SECONDARY_MENU_ITEMS.map((item) => (
                <DropdownItem key={item.label} item={item} onClick={handleItemClick} />
              ))}
            </MenuList>
          </Paper>
        </Portal>
      )}
    </>
  );
};

export default HamburgerMenu;
